// Angular
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { fetch } from '@nx/angular';

// Third party
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { map, take } from 'rxjs';

// Local library
import {
  CommonFacade,
  HOME_DIRECTORY,
  PusherService,
  SessionService,
  ToteAccountAPIService,
  TransactionAPIService,
} from '@amwager/data-access';
import { AccountFacade } from '@amwager/account/data-access';
import { Web } from '@amwager/util-ts';

// Local file
import * as FundingActions from './funding.actions';
import { FundingFacade } from './funding.facade';

@Injectable()
export class FundingEffects {

  constructor(
    @Inject(HOME_DIRECTORY) private readonly homeDirectory: string,
    private readonly actions$: Actions,
    private readonly router: Router,
    private readonly fundingFacade: FundingFacade,
    private readonly sessionService: SessionService,
    private readonly pusherService: PusherService,
    private readonly commonFacade: CommonFacade,
    private readonly transactionAPIService: TransactionAPIService,
    private readonly toteAccountAPIService: ToteAccountAPIService,
    private readonly accountFacade: AccountFacade
  ) {}

  // init$ = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(FundingActions.initFunding),
  //     switchMap(() => of(FundingActions.loadFundingSuccess({ funding: [] }))),
  //     catchError((error) => {
  //       console.error('Error', error);
  //       return of(FundingActions.loadFundingFailure({ error }));
  //     })
  //   )
  // );

  init$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FundingActions.initFundingState),
      fetch({
        id: () => 'init-funding',
        run: () => FundingActions.loadFundingStateSuccess(),
        /* eslint-disable no-empty-pattern */
        onError: ({}, error) => FundingActions.loadFundingStateFailure({ error }),
      })
    )
  );

  depositDebitCredit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FundingActions.depositDebitCreditAPI),
      fetch({
        id: () => 'deposit-credit-debit-card',
        run: ({ amount, orderId, tmsId }) => this.transactionAPIService.creditCardDeposit$({
          Amount: amount,
          OrderId: orderId,
          Tmsid: tmsId
        })
          .pipe(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map(( response: any ) => {

              this.accountFacade.getBalance("WagerEffects:depositDebitCredit");

              return FundingActions.depositDebitCreditAPISuccess({ successResponse: response });
            })
          )
        ,
        onError: (param, errorResponse) => {
          return FundingActions.depositDebitCreditAPIFailed({ errorResponse });
        }
      })
    )
  );

  getDebitCredit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FundingActions.getDebitCreditAPI),
      fetch({
        id: () => 'get-debit-credit',
        run: () => this.transactionAPIService.eprotectInformation$()
          .pipe(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map(( response: any ) => {
              // if (response !== undefined)
              //   this.fundingFacade.setCards(response);
              // else
              //   this.fundingFacade.setCards([]);
              return FundingActions.getDebitCreditAPISuccess({ successResponse: response });
            })
          )
        ,
        onError: (param, errorResponse) => {
          return FundingActions.getDebitCreditAPIFailed({ errorResponse });
        }
      })
    )
  );

  addDebitCredit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FundingActions.addDebitCreditAPI),
      fetch({
        id: () => 'add-debit-credit',
        run: ({
          card
        }) => this.transactionAPIService.eprotectInformationPost$(card)
          .pipe(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
            map(( response: any ) => {
              // console.log(card);
              return FundingActions.addDebitCreditAPISuccess({
                newCard: card, successResponse: response
              });
            })
          )
        ,
        onError: (param, errorResponse) => {
          return FundingActions.addDebitCreditAPIFailed({ errorResponse });
        }
      })
    )
  );

  deleteDebitCredit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FundingActions.deleteDebitCreditAPI),
      fetch({
        id: () => 'delete-debit-credit',
        run: ({ orderId }) => this.transactionAPIService.eprotectInformationDelete$(
          orderId
        )
          .pipe(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
            map(( response: any ) => {
              // console.log(orderId);
              return FundingActions.deleteDebitCreditAPISuccess({
                orderId, successResponse: response
              });
            })
          )
        ,
        onError: (param, errorResponse) => {
          return FundingActions.deleteDebitCreditAPIFailed({ errorResponse });
        }
      })
    )
  );

  transactionFeeAPI$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FundingActions.getTransactionFeeAPI),
      fetch({
        id: () => 'transaction-fee',
        run: ({ method, isDeposit, amount }) => this.transactionAPIService.getTransactionFee$(
          method, isDeposit, amount
        ).pipe(
            map((successResponse: unknown ) => {
              return FundingActions.getTransactionFeeAPISuccess({
                successResponse
              });
            })
        ),
        onError: (param, errorResponse) => {
          return FundingActions.getTransactionFeeAPIFailed({ errorResponse });
        }
      })
    )
  );

  depositPromoCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FundingActions.depositPromoCodeAPI),
      fetch({
        id: () => 'deposit-promo-code',
        run: ({ promoCode }) => this.transactionAPIService.promoCodeDeposit$(
          promoCode
        )
          .pipe(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map(( response: any ) => {
              this.accountFacade.getBalance("WagerEffects:depositPromoCode");
              return FundingActions.depositPromoCodeAPISuccess({ successResponse: response });
            })
          )
        ,
        onError: (param, errorResponse) => {
          return FundingActions.depositPromoCodeAPIFailed({ errorResponse });
        }
      })
    )
  );

  depositMoneyPak$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FundingActions.depositMoneyPakAPI),
      fetch({
        id: () => 'deposit-money-pak',
        run: ({ moneyPakNumber }) => this.transactionAPIService.moneyPakDeposit$({
          MoneyPakNumber: String(moneyPakNumber)
        })
          .pipe(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map(( response: any ) => {
              this.accountFacade.getBalance("WagerEffects:depositMoneyPak");
              return FundingActions.depositMoneyPakAPISuccess({ successResponse: response });
            })
          )
        ,
        onError: (param, errorResponse) => {
          return FundingActions.depositMoneyPakAPIFailed({ errorResponse });
        }
      })
    )
  );

  depositCryptoCurrency$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FundingActions.depositCryptoCurrencyAPI),
      fetch({
        id: () => 'deposit-crypto-currency',
        run: ({ amount }) => this.transactionAPIService.cryptoDeposit$({
          Amount: amount
        })
          .pipe(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map(( response: any ) => {

              this.accountFacade.getBalance("WagerEffects:depositCryptoCurrency");

              return FundingActions.depositCryptoCurrencyAPISuccess({ successResponse: response });
            })
          )
        ,
        onError: (param, errorResponse) => {
          return FundingActions.depositCryptoCurrencyAPIFailed({ errorResponse });
        }
      })
    )
  );

  getFundingLimit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FundingActions.getFundingLimitAPI),
      fetch({
        id: () => 'get-funding-limit',
        run: () => this.transactionAPIService.getFundingLimits$()
          .pipe(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map(( response: any ) => {

              try {
                if ("Model" in response) {
                  response = response?.Model || [];
                }
              } catch (error) {
                console.error("Model:API");
              }

              return FundingActions.getFundingLimitAPISuccess({ successResponse: response });
            })
          )
        ,
        onError: (param, errorResponse) => {
          return FundingActions.getFundingLimitAPIFailed({ errorResponse });
        }
      })
    )
  );

  getPendingAch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FundingActions.getPendingAchAPI),
      fetch({
        id: () => 'get-pending-ach',
        run: () => this.transactionAPIService.getPendingACH$()
          .pipe(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map(( response: any ) => {
              return FundingActions.getPendingAchAPISuccess({ successResponse: response });
            })
          )
        ,
        onError: (param, errorResponse) => {
          return FundingActions.getPendingAchAPIFailed({ errorResponse });
        }
      })
    )
  );

  depositOnlineCheck$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FundingActions.depositOnlineCheckAPI),
      fetch({
        id: () => 'deposit-online-check-ach',
        run: ({ amount, bankAccountId, tmsId }) => this.transactionAPIService.aCHDeposit$({
          Amount: amount,
          BankAccountId: bankAccountId,
          Tmsid: tmsId
        })
          .pipe(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map(( response: any ) => {

              this.accountFacade.getBalance("FundingEffects:depositOnlineCheck");

              if (!response?.Error) {
                this.fundingFacade.addPendingAch(
                  {
                    Amount: amount,
                    Date: new Date()
                  },
                  response
                );
              }

              return FundingActions.depositOnlineCheckAPISuccess({ successResponse: response });
            })
          )
        ,
        onError: (param, errorResponse) => {
          return FundingActions.depositOnlineCheckAPIFailed({ errorResponse });
        }
      })
    )
  );

  getAchAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FundingActions.getAchAccountAPI),
      fetch({
        id: () => 'get-ach-account',
        run: () => this.transactionAPIService.getAllACHAccounts$()
        // run: () => this.toteAccountAPIService.getAllACHAccounts$()
          .pipe(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map(( response: any ) => {

              try {
                if ("Model" in response) {
                  response = response?.Model?.ACHAccounts || [];
                }
              } catch (error) {
                console.error("Model:API");
              }

              return FundingActions.getAchAccountAPISuccess({ successResponse: response });
            })
          )
        ,
        onError: (param, errorResponse) => {
          return FundingActions.getAchAccountAPIFailed({ errorResponse });
        }
      })
    )
  );

  addAchAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FundingActions.addAchAccountAPI),
      fetch({
        id: () => 'add-ach-account',
        run: ({
          account
        }) => this.transactionAPIService.addACHAccount$({
          AchAccount: account.AchAccount,
          AchRoute: account.AchRoute,
          AchAccounttype: account.AchAccounttype,
          AchName: account.AchName
        })
          .pipe(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
            map(( response: any ) => {

              return FundingActions.addAchAccountAPISuccess({
                newAccount: account, successResponse: response
              });
            })
          )
        ,
        onError: (param, errorResponse) => {
          return FundingActions.addAchAccountAPIFailed({ errorResponse });
        }
      })
    )
  );

  deleteAchAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FundingActions.deleteAchAccountAPI),
      fetch({
        id: () => 'delete-ach-account',
        run: ({ accountId }) => this.transactionAPIService.deleteACHAccount$({
          ACHAccountId: accountId
        })
          .pipe(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
            map(( response: any ) => {
              return FundingActions.deleteAchAccountAPISuccess({
                accountId, successResponse: response
              });
            })
          )
        ,
        onError: (param, errorResponse) => {
          return FundingActions.deleteAchAccountAPIFailed({ errorResponse });
        }
      })
    )
  );

  withdrawDebitCredit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FundingActions.withdrawDebitCreditAPI),
      fetch({
        id: () => 'withdraw-credit-debit-card',
        run: ({ amount, orderId, tmsId }) => this.transactionAPIService.creditCardWithdraw$({
          Amount: amount,
          OrderId: orderId,
          Tmsid: tmsId
        })
          .pipe(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map(( response: any ) => {

              this.accountFacade.getBalance("WagerEffects:withdrawDebitCredit");

              return FundingActions.withdrawDebitCreditAPISuccess({ successResponse: response });
            })
          )
        ,
        onError: (param, errorResponse) => {
          return FundingActions.withdrawDebitCreditAPIFailed({ errorResponse });
        }
      })
    )
  );

  withdrawCheckByEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FundingActions.withdrawCheckByEmailAPI),
      fetch({
        id: () => 'withdraw-check-by-email',
        run: ({ method, amount }) => this.toteAccountAPIService.addManualWithdrawlRequest$({
          Method: method,
          Amount: amount
        })
          .pipe(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map(( response: any ) => {

              this.accountFacade.getBalance("WagerEffects:withdrawCheckByEmail");

              return FundingActions.withdrawCheckByEmailAPISuccess({ successResponse: response });
            })
          )
        ,
        onError: (param, errorResponse) => {
          return FundingActions.withdrawCheckByEmailAPIFailed({ errorResponse });
        }
      })
    )
  );

  withdrawOnlineCheck$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FundingActions.withdrawOnlineCheckAPI),
      fetch({
        id: () => 'withdraw-online-check-ach',
        run: ({ amount, bankAccountId, tmsId }) => this.transactionAPIService.aCHWithdrawal$({
          Amount: amount,
          BankAccountId: bankAccountId,
          Tmsid: tmsId
        })
          .pipe(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map(( response: any ) => {

              this.accountFacade.getBalance("WagerEffects:withdrawOnlineCheck");
              return FundingActions.withdrawOnlineCheckAPISuccess({ successResponse: response });
            })
          )
        ,
        onError: (param, errorResponse) => {
          return FundingActions.withdrawOnlineCheckAPIFailed({ errorResponse });
        }
      })
    )
  );

}
